import { useCallback, useEffect, useRef, useState } from 'react';
import { requirementsApi } from '../services/api';
import { enqueueSave, dequeueSave, getQueuedSave, getAllQueued } from '../utils/offlineQueue';

// Module-level (stable) default so an unspecified `isOnline` doesn't recreate
// a new function identity on every render — a fresh identity there would
// cascade through attemptSave/flushQueue's useCallback deps and re-run the
// online/offline-listener effect (and its "flush on mount if already online"
// check) on every keystroke instead of once.
const defaultIsOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * Debounced network autosave + offline queue/retry for the Item Detail editor
 * (#160 — the gap #145 explicitly left open: #145 shipped a LOCAL draft safety
 * net + a save-status indicator, but real writes still required the explicit
 * Save button, and there was no offline handling at all).
 *
 * - Debounce: `debounceMs` after the last relevant edit (driven by `getPayload`
 *   changing identity — the caller re-creates it from title/description/
 *   attributes, same idiom as useDebouncedValue), attempt a real save.
 * - Offline queue: if the browser is offline, or the attempt fails with no
 *   response (network/timeout — not a 409, not a validation error), the edit
 *   is queued (utils/offlineQueue.js) instead of lost, and retried on the
 *   `online` event. A 409 is routed to `onConflict` — the existing conflict
 *   banner — not treated as a connectivity failure.
 * - Status: 'idle' | 'saving' | 'saved' | 'offline-queued' | 'reconnecting' | 'error'.
 *
 * Deliberately scoped to title/description/attributes (the fields the Item
 * Detail form edits inline) — NOT pending relationship removals, which stay an
 * explicit, confirmable action on the Save button (see ItemDetails.js).
 */
export function useAutosave({
  itemId,
  dirty,
  getPayload,
  onSaved,
  onConflict,
  debounceMs = 2000,
  saveFn = requirementsApi.updateItem,
  isOnline = defaultIsOnline,
}) {
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);
  const itemIdRef = useRef(itemId);
  itemIdRef.current = itemId;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const onConflictRef = useRef(onConflict);
  onConflictRef.current = onConflict;

  // Attempt one save. `routeCallbacks` gates whether onSaved/onConflict fire —
  // used to suppress them when flushing a queued edit for an item that is no
  // longer the one currently open (its state has moved on; just sync storage).
  const attemptSave = useCallback(async (id, payload, { routeCallbacks = true } = {}) => {
    if (!isOnline()) {
      enqueueSave(id, payload);
      if (routeCallbacks) setStatus('offline-queued');
      return;
    }
    if (routeCallbacks) setStatus('saving');
    try {
      const saved = await saveFn(id, payload.apiPayload);
      dequeueSave(id);
      if (routeCallbacks) {
        setStatus('saved');
        if (onSavedRef.current) onSavedRef.current(saved, payload.formSnapshot);
      }
      return { ok: true };
    } catch (err) {
      if (err?.response?.status === 409 && err.response?.data?.detail) {
        // Stale precondition — the queued/debounced write no longer applies
        // cleanly. Drop it (blind-retrying the same payload would just 409
        // again) and hand off to the normal conflict-resolution UI.
        dequeueSave(id);
        if (routeCallbacks) {
          setStatus('error');
          if (onConflictRef.current) onConflictRef.current(err.response.data.detail);
        }
        return { ok: false, conflict: true };
      }
      if (!err?.response) {
        // No response reached us at all — offline, DNS failure, timeout.
        // A connectivity problem, not a hard error: queue and retry later.
        enqueueSave(id, payload);
        if (routeCallbacks) setStatus('offline-queued');
        return { ok: false, offline: true };
      }
      if (routeCallbacks) setStatus('error');
      return { ok: false };
    }
  }, [saveFn, isOnline]);

  // Debounced trigger — resets on every call with a new `getPayload` identity
  // (the caller re-creates it whenever title/description/attributes change),
  // same pattern as useDebouncedValue's settle-then-fire.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!itemId || !dirty || !getPayload) return undefined;
    timerRef.current = setTimeout(() => {
      const payload = getPayload();
      if (payload) attemptSave(itemId, payload);
    }, debounceMs);
    return () => clearTimeout(timerRef.current);
  }, [itemId, dirty, debounceMs, getPayload, attemptSave]);

  // Reconnect retry: flush EVERY queued item (not just the one currently
  // open) — an edit queued while viewing item A should still land if the
  // user has since navigated to item B by the time connectivity returns.
  const flushQueue = useCallback(async () => {
    const entries = getAllQueued();
    if (entries.length === 0) return;
    const openId = itemIdRef.current;
    if (entries.some((e) => e.itemId === openId)) setStatus('reconnecting');
    for (const entry of entries) {
       
      const result = await attemptSave(entry.itemId, entry.payload, { routeCallbacks: entry.itemId === openId });
      if (result?.offline) break; // still offline (or flapping) — stop, retry on the next 'online'
    }
  }, [attemptSave]);

  useEffect(() => {
    const handleOnline = () => { flushQueue(); };
    const handleOffline = () => {
      if (dirtyRef.current || getQueuedSave(itemIdRef.current)) setStatus('offline-queued');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Cover "closed the tab while offline, reopened after reconnect" — flush
    // whatever is already queued on mount if we're online now. Registered
    // once: `flushQueue`/`isOnline` are stable across renders (see
    // defaultIsOnline above), so this doesn't re-run on every keystroke.
    if (isOnline()) flushQueue();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [flushQueue, isOnline]);

  // Switching items: the new item's own state takes over — don't carry a
  // stale 'saving'/'error' badge across.
  useEffect(() => { setStatus(getQueuedSave(itemId) ? 'offline-queued' : 'idle'); }, [itemId]);

  const retryNow = useCallback(() => {
    const queued = getQueuedSave(itemIdRef.current);
    if (queued) attemptSave(itemIdRef.current, queued.payload);
  }, [attemptSave]);

  return { status, retryNow };
}

export default useAutosave;
